// The add, claim, and drop sheet (WAIVERS.md "The claim flow", "The add
// and drop flow"): one DetailSheet, three modes, every consequence spelled
// out before the tap that commits. Adds and drops get an undo toast.
//
// TWO WORLDS, ONE SHEET (W8). The practice league's sheet is the simulator's
// (`useSim`), as it always was. A real league's sheet reads
// `GET …/players/:playerId/move` and writes the move to the server — the add
// or drop with an `Idempotency-Key` minted for this action and sent again only
// when this same action is tried again (`AttemptKeys`, B-150), the claim the
// same way, an edit as a PUT. Whatever the server refuses is shown in its own
// sentence, in the sheet, and the sheet stays open to fix it.

import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import { tourDone } from '../../demo/chapters';
import {
  failureOf,
  valueOf,
  whenInZone,
  type ClaimRequest,
  type DropOption,
  type RosterMoveView,
} from '@fsp/domain';
import { useLeagueZoneState, useMoveSheet, useReaderZone } from '../../data/hooks';
import { useSim } from '../../data/SimProvider';
import { useLeagueSource } from '../../data/leagueSource';
import { messageOf, type Reply } from '../../data/api';
import { AttemptKeys, addDrop, editClaim, placeClaim, undoMove } from '../../data/moves';
import { useDelight } from '../../delight/useDelight';
import { GameLabel, PositionPill, ProTeamCode, StatusBadge } from '../../kit';
import {
  Button,
  Caption,
  Choice,
  IconButton,
  Line,
  NumberField,
  Stack,
  Text,
  tell,
  tellWithUndo,
} from '../../kit/ui';
import { DetailSheet } from '../../templates/DetailSheet';
import { num } from './notComputed';
import { Proj } from './Projection';
import { useTools } from '../../data/tools';

export interface RosterMove {
  kind: 'add' | 'claim' | 'drop';
  playerId: string;
  /** Set to edit a pending claim. */
  claimId?: string | null;
}

/**
 * An instant in the reader's zone, named (B-181), read now: the weekday alone while that
 * names one day, the date too once it could name the wrong week
 * (`whenInZone`, the one rule for every time on these screens). Null is a
 * time nobody has set.
 */
export function fmtWhen(iso: string | null, timeZone: string, long = false): string {
  if (!iso) return 'later';
  return whenInZone(iso, timeZone, Date.now(), long ? 'long' : 'short');
}

/**
 * Show an add or drop toast with an Undo button that lives as long as the
 * toast. `undo` answers the sentence to show when it could not be undone, or
 * null when it was — at once for the simulator, after the server has answered
 * for a real league.
 */
export function showUndoToast(
  message: string,
  undo: () => string | null | Promise<string | null>,
  color: 'green' | 'red' = 'green',
): void {
  tellWithUndo(message, undo, color === 'red' ? 'bad' : 'good');
}

/**
 * Where the drop picked on an add or a claim goes, before it is confirmed
 * (CLAUDE.md: say what will happen). The drop sheet has the server's own
 * sentence (`dropConsequence`); the picker's rows say only what is unusual
 * about a player, and a recent add skipping waivers is one of those (B-138),
 * so this line says the rest: on waivers until when, where anyone can claim
 * him, or a free agent at once in a league that sends no drop to waivers.
 * A claim's drop happens at its run, and says so — and with the date when
 * the weekday alone would name the wrong week: a drop made at Wednesday's run
 * clears the Wednesday after (found walking W8; `fmtWhen` holds the rule).
 */
export function dropLine(option: DropOption, zone: string, kind: 'add' | 'claim'): string | null {
  const to = option.dropsTo;
  if (to === null) return null;
  const who = option.player.name;
  const lead = kind === 'claim' ? `If the claim wins, ${who}` : who;
  if (to.kind === 'waivers')
    return `${lead} will sit on waivers until ${fmtWhen(to.clearsAt, zone, true)}, where anyone can put in a claim.`;
  // The row above already says a recent add goes straight to free agency.
  if (to.because === 'recent-add') return null;
  return `${lead} becomes a free agent right away.`;
}

export function RosterMoveSheet({
  move,
  showProjection: asked,
  onClose,
}: {
  move: RosterMove | null;
  /**
   * Whether the sheet carries projections — the player's and each drop
   * candidate's. THE SCREEN THAT OPENS IT DECIDES, because the sheet opens
   * over that screen and is part of its task (PLAYERS.md "The action sheet"):
   * Waivers gives the projection to Standard (DETAIL_LEVELS §Waivers), Players
   * has it on its Simple face, and a player's card is not leveled (B-177).
   * None in a league that keeps no projections, whatever the screen asks
   * (RULES_SCHEMA §15c).
   */
  showProjection: boolean;
  onClose: () => void;
}) {
  const tools = useTools();
  const showProjection = asked && tools.projections;
  const snapshot = useMoveSheet(move);
  const clock = useLeagueZoneState();
  const zone = useReaderZone();
  const view = snapshot === null ? null : valueOf(snapshot);
  if (move === null || snapshot === null) {
    return <DetailSheet opened={false} onClose={onClose} children={null} />;
  }
  // A SHEET WAITS FOR ITS ZONE as well as its view: every time on it is the
  // reader's (B-181), and for an account with none, the league's, which may
  // still be being read — a time in another zone is a wrong time.
  // And a read that failed says so, with a way to ask again, rather than
  // waiting for good (the review of W8: one dropped read of the league's
  // clock left every sheet on "Reading the move…").
  if (view === null || zone === null) {
    const failure = failureOf(snapshot)?.message ?? clock.failure;
    return (
      <DetailSheet opened onClose={onClose} title={failure ? 'Not now' : 'One moment'}>
        <Stack gap={3}>
          <Text tone={failure ? 'primary' : 'secondary'} block>
            {failure ?? 'Reading the move…'}
          </Text>
          {clock.failure !== null ? (
            <div>
              <Button size="xs" onClick={clock.retry}>
                Try again
              </Button>
            </div>
          ) : null}
        </Stack>
      </DetailSheet>
    );
  }
  return (
    <MoveBody
      key={keyOf(move)}
      view={view}
      zone={zone}
      showProjection={showProjection}
      onClose={onClose}
    />
  );
}

function keyOf(move: RosterMove): string {
  return `${move.kind}:${move.playerId}:${move.claimId ?? ''}`;
}

function MoveBody({
  view,
  zone,
  showProjection,
  onClose,
}: {
  view: RosterMoveView;
  zone: string;
  showProjection: boolean;
  onClose: () => void;
}) {
  const sim = useSim();
  const real = useLeagueSource() === 'api';
  const { leagueId } = useParams();
  const { play } = useDelight();
  const existing = view.existing;
  // Fresh defaults for this move; an edited claim prefills. `MoveBody` is
  // keyed on the move, so a different move is a different body.
  const [bid, setBid] = useState<number>(existing?.bid ?? view.bid?.min ?? 0);
  const [dropId, setDropId] = useState<string | null>(existing?.dropPlayerIds[0] ?? null);
  const [order, setOrder] = useState(existing?.order ?? view.defaultOrder);
  const [conditional, setConditional] = useState<string | null>(
    existing?.conditionalOnClaimId ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  // ONE WRITE AT A TIME: a second tap before the answer is not a second move.
  const [saving, setSaving] = useState(false);
  const attempts = useRef(new AttemptKeys());
  // The simulator's view recomputes each minute and can arrive after the body
  // mounted with a different default order; keep the stepper inside the list.
  useEffect(() => {
    setOrder((o) => Math.min(Math.max(1, o), view.otherClaims.length + 1));
  }, [view.otherClaims.length]);

  const p = view.player;
  const when = fmtWhen(view.processesAt, zone);
  const title =
    view.kind === 'add'
      ? `Add ${p.name}`
      : view.kind === 'drop'
        ? `Drop ${p.name}?`
        : `${existing ? 'Edit claim for' : 'Claim'} ${p.name}`;
  const claimCount = view.otherClaims.length + 1;
  const needsDrop = view.kind !== 'drop' && view.mustDrop && !dropId;
  const chosen = dropId === null ? null : (view.roster.find((o) => o.player.id === dropId) ?? null);
  const chosenLine =
    chosen !== null && view.kind !== 'drop' ? dropLine(chosen, zone, view.kind) : null;

  const failed = (reply: Reply<unknown>) =>
    setError(
      messageOf(
        reply,
        'That did not go through.',
        // THE MOVE MAY HAVE BEEN MADE: the request can land and its answer
        // be lost. The same press again is safe — the same key goes with it
        // (`AttemptKeys`), and the server answers with the move it made.
        'Could not reach the site, so we cannot tell whether that went through. Press it again: the same move is never made twice.',
      ),
    );

  // CLOSED BEFORE THE LEAGUE IS READ AGAIN. The write asks every read of the
  // league to refresh once it has answered (`data/moves.ts`), and this sheet
  // is one of them: closed in the ordinary way, it was asked to read itself
  // again and then aborted that read as it went (found walking W8).
  const closeNow = () => flushSync(onClose);

  const confirmReal = async () => {
    if (leagueId === undefined) return;
    const drops = dropId ? [dropId] : [];
    if (view.kind === 'add' || view.kind === 'drop') {
      const body = view.kind === 'add' ? { add: p.id, drop: drops } : { drop: [p.id] };
      const reply = await addDrop(leagueId, body, attempts.current.keyFor(body));
      attempts.current.settled(reply);
      if (reply.status !== 'ok') return failed(reply);
      play('lock');
      if (view.kind === 'add') tourDone('add');
      const tx = reply.data;
      // The practice league's words, from what the server says was moved.
      const names = (list: typeof tx.added) => list.map((x) => x.name).join(', ');
      showUndoToast(
        [
          tx.added.length ? `Added ${names(tx.added)}.` : null,
          tx.dropped.length ? `Dropped ${names(tx.dropped)}.` : null,
        ]
          .filter(Boolean)
          .join(' '),
        async () => {
          const undone = await undoMove(leagueId, tx.id);
          return undone.status === 'ok'
            ? null
            : messageOf(
                undone,
                'That could not be undone.',
                'Could not reach the site, so we cannot tell whether that was undone. Try again: if it was, you will be told so.',
              );
        },
        view.kind === 'drop' ? 'red' : 'green',
      );
      closeNow();
      return;
    }
    const body: ClaimRequest = {
      playerId: p.id,
      bid: view.bid ? bid : null,
      dropPlayerIds: drops,
      conditionalOnClaimId: conditional,
      order,
    };
    const reply = existing
      ? await editClaim(leagueId, existing.claimId, body)
      : await placeClaim(leagueId, body, attempts.current.keyFor(body));
    attempts.current.settled(reply);
    if (reply.status !== 'ok') return failed(reply);
    play('lock');
    tourDone('claim');
    const at = fmtWhen(reply.data.processesAt, zone);
    tell(
      existing
        ? `Claim updated. Processes ${at}.`
        : `Claim placed. Processes ${at}; the result will be in the log.`,
      'good',
    );
    closeNow();
  };

  const confirmSim = () => {
    const drops = dropId ? [dropId] : [];
    if (view.kind === 'add') {
      const r = sim.addPlayer(p.id, drops);
      if (!r.ok) return setError(r.reason);
      tourDone('add');
      play('lock');
      const dropped = drops.map((id) => view.roster.find((o) => o.player.id === id)?.player.name);
      showUndoToast(
        `Added ${p.name}.${dropped.length ? ` Dropped ${dropped.join(', ')}.` : ''}`,
        () => sim.undoTransaction(r.transaction.id),
      );
      onClose();
      return;
    }
    if (view.kind === 'drop') {
      const r = sim.dropPlayer(p.id);
      if (!r.ok) return setError(r.reason);
      play('lock');
      showUndoToast(`Dropped ${p.name}.`, () => sim.undoTransaction(r.transaction.id), 'red');
      onClose();
      return;
    }
    const r = sim.placeClaim({
      playerId: p.id,
      bid: view.bid ? bid : null,
      dropPlayerIds: drops,
      conditionalOnClaimId: conditional,
      order,
      claimId: existing?.claimId,
    });
    if (!r.ok) return setError(r.reason);
    play('lock');
    tourDone('claim');
    tell(
      existing
        ? `Claim updated. Processes ${when}.`
        : `Claim placed. Processes ${when}; the result will be in the log.`,
      'good',
    );
    onClose();
  };

  const confirm = () => {
    setError(null);
    if (!real) return confirmSim();
    if (saving) return;
    setSaving(true);
    void confirmReal().finally(() => setSaving(false));
  };

  return (
    <DetailSheet opened onClose={onClose} title={title}>
      <Stack gap={4}>
        <Line gap={3} align="start">
          <PositionPill pos={p.positions[0]!} />
          <div className="fsp-card-who">
            <div className="fsp-disp fsp-card-name">{p.name}</div>
            <Line gap={1} wrap>
              <ProTeamCode code={p.proTeam} />
              <StatusBadge player={p} />
              <GameLabel game={view.game} />
              {showProjection ? (
                <span className="fsp-sub fsp-num">proj {num(view.projection)}</span>
              ) : null}
            </Line>
          </div>
        </Line>

        {view.blocked ? (
          <div className="fsp-surface fsp-move-blocked">{view.blocked}</div>
        ) : (
          <>
            {view.kind === 'claim' ? (
              <Text tone="secondary" block>
                {view.processesAt
                  ? `Processes ${fmtWhen(view.processesAt, zone, true)}.`
                  : 'No waiver run is scheduled yet.'}
                {view.bid ? ' Bids are sealed until then.' : ''}
              </Text>
            ) : null}

            {view.kind === 'claim' && view.bid ? (
              <Stack gap={1}>
                <Caption>Bid</Caption>
                <Line gap={3}>
                  <span className="fsp-move-bid">
                    <NumberField
                      label="Bid"
                      hideLabel
                      value={bid}
                      onChange={(v) => setBid(v ?? 0)}
                      min={view.bid.min}
                      max={view.bid.max}
                      prefix="$"
                    />
                  </span>
                  <div className="fsp-sub fsp-ui-wrap-text">
                    {/* The whole budget: a pending claim holds none of it back
                        (B-141). This read "$40 available after your other
                        claims · $62 left in all" until W3. */}
                    ${view.bid.left} left
                    {view.bid.min > 0 ? ` · minimum $${view.bid.min}` : ''}
                    {!view.bid.allowZero ? ' · no $0 bids' : ''}
                  </div>
                </Line>
              </Stack>
            ) : null}

            {view.kind !== 'drop' ? (
              <Stack gap={1}>
                <Caption>
                  {view.mustDrop ? 'Drop someone (roster full)' : 'Drop someone (optional)'}
                </Caption>
                <DropPicker
                  roster={view.roster}
                  value={dropId}
                  optional={!view.mustDrop}
                  showProjection={showProjection}
                  onChange={setDropId}
                />
              </Stack>
            ) : null}

            {view.kind === 'claim' && claimCount > 1 ? (
              <Line gap={2}>
                <Caption>Try this claim</Caption>
                <IconButton
                  icon="remove"
                  label="Earlier"
                  variant="quiet"
                  onClick={() => setOrder((o) => Math.max(1, o - 1))}
                  disabled={order <= 1}
                />
                <Text weight="bold" numeric>
                  {ordinal(order)} of {claimCount}
                </Text>
                <IconButton
                  icon="add"
                  label="Later"
                  variant="quiet"
                  onClick={() => setOrder((o) => Math.min(claimCount, o + 1))}
                  disabled={order >= claimCount}
                />
              </Line>
            ) : null}

            {view.kind === 'claim' && view.conditionalAllowed ? (
              <Choice
                label="Only if another claim fails"
                description="A conditional claim never spends budget on a player you would not have wanted."
                placeholder="No condition"
                searchable={false}
                clearable
                value={conditional}
                onChange={setConditional}
                options={view.otherClaims.map((c) => ({
                  value: c.claimId,
                  label: `Only if my claim for ${c.playerName} fails`,
                }))}
              />
            ) : null}

            {view.kind === 'drop' && view.dropConsequence ? (
              <Text block>{view.dropConsequence}</Text>
            ) : null}
            {chosenLine ? <Text block>{chosenLine}</Text> : null}

            {error ? (
              <div role="alert">
                <Text tone="bad">{error}</Text>
              </div>
            ) : null}

            <Button
              full
              size="md"
              variant={view.kind === 'drop' ? 'danger' : 'primary'}
              disabled={needsDrop}
              loading={saving}
              onClick={confirm}
            >
              {view.kind === 'add'
                ? dropId
                  ? 'Add and drop'
                  : 'Add'
                : view.kind === 'drop'
                  ? `Drop ${p.shortName}`
                  : existing
                    ? 'Save claim'
                    : view.bid
                      ? `Claim for $${bid}`
                      : 'Place claim'}
            </Button>
            {needsDrop ? (
              <Text tone="secondary" size="xs" block center>
                Pick someone to drop first.
              </Text>
            ) : null}
          </>
        )}
      </Stack>
    </DetailSheet>
  );
}

/** My roster as radio rows, each with the consequence of dropping that player. */
function DropPicker({
  roster,
  value,
  optional,
  showProjection,
  onChange,
}: {
  roster: DropOption[];
  value: string | null;
  optional: boolean;
  showProjection: boolean;
  onChange: (id: string | null) => void;
}) {
  return (
    <div className="fsp-surface" role="radiogroup" aria-label="Drop">
      {optional ? (
        <div
          className={`fsp-row fsp-row-clickable fsp-drop-row fsp-drop-none ${value === null ? 'fsp-row-selected' : ''}`}
          role="radio"
          aria-checked={value === null}
          tabIndex={0}
          onClick={() => onChange(null)}
          onKeyDown={(e) => (e.key === 'Enter' ? onChange(null) : undefined)}
        >
          <span className="fsp-muted">—</span>
          <span>No drop</span>
        </div>
      ) : null}
      {roster.map((o) => {
        const selected = value === o.player.id;
        return (
          <div
            key={o.player.id}
            className={`fsp-row fsp-drop-row ${showProjection ? 'fsp-drop-projected' : ''} ${o.droppable ? 'fsp-row-clickable' : 'fsp-row-dim'} ${selected ? 'fsp-row-selected' : ''}`}
            role="radio"
            aria-checked={selected}
            aria-disabled={!o.droppable}
            tabIndex={o.droppable ? 0 : -1}
            onClick={() => (o.droppable ? onChange(selected ? null : o.player.id) : undefined)}
            onKeyDown={(e) =>
              e.key === 'Enter' && o.droppable ? onChange(o.player.id) : undefined
            }
          >
            <PositionPill pos={o.slot} size="sm" />
            <div className="fsp-drop-body">
              <div className="fsp-name">
                <span className="fsp-name-text">{o.player.name}</span>
                <ProTeamCode code={o.player.proTeam} />
                <StatusBadge player={o.player} />
              </div>
              <div
                className={`fsp-sub fsp-ui-wrap-text ${o.consequence ? 'fsp-drop-consequence' : ''}`}
              >
                {o.consequence ?? <GameLabel game={o.game} />}
              </div>
            </div>
            {showProjection ? (
              <div className="fsp-right fsp-num fsp-sub">
                <Proj>{num(o.projection)}</Proj>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'] as const;
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}
